import { date } from "quasar";
import { numberFormat } from "assets/scripts/utilities/helpers";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";

const { formatDate } = date;

export function useAssociatedPaymentsData({ formData }) {
  const color = useColor();

  const { toggleAssociatedDataDrawer, getCreditDebitNotesForPR } =
    usePaymentRequestStore();

  const { viewFile } = useFile();

  const getTableData = async (type) => {
    try {
      if (!unref(formData)[type]?.length) {
        return [];
      }

      if (type === "files" || type === "associated_payments") {
        return unref(formData)[type];
      }

      const options = {
        params: {
          _id: unref(formData)
            [type].map((note) => note._id)
            .join(","),
          type,
        },
      };
      const rows = await getCreditDebitNotesForPR(options);

      return rows;
    } catch (error) {
      console.error(error);

      return [];
    }
  };

  const columns = computed(() => {
    return [
      {
        label: "Number",
        field: "number",
        cellClass: "text-primary text-weight-medium underline cursor-pointer",
        onClick: (row) => {
          if (!row.file) {
            return;
          }

          viewFile(row.file);
        },
      },
      {
        label: "Date",
        field: "created_date",
        format: (value) => formatDate(value, "DD MMM YYYY, h:mm a") || "-",
      },
      {
        name: "Net Amount",
        label: "Net Amount",
        field: "total_amount",
        format: (value) =>
          numberFormat({ number: value ?? 0, style: "currency" }) || "-",
        sortable: true,
      },
    ];
  });

  const filesColumns = computed(() => {
    return [
      {
        label: "File Type",
        field: "file_type",
      },
      {
        label: "Name",
        field: "file_name",
        cellClass: "text-primary text-weight-medium underline cursor-pointer",
        onClick: viewFile,
      },
    ];
  });

  const paymentsMadeColumns = computed(() => {
    return [
      {
        label: "Date",
        field: "created_date",
        format: (value) => formatDate(value, "DD MMM YYYY, h:mm a") || "-",
      },
      {
        label: "Payment#",
        field: "internal_number",
      },
      {
        label: "Amount",
        field: "net_payable",
        format: (value) =>
          numberFormat({ number: value ?? 0, style: "currency" }) || "-",
      },
      {
        label: "Status",
        field: "status_cd",
        format: (status_cd) =>
          status_cd
            ? `<span class="bg-${color(status_cd).color} text-${
                color(status_cd).textColor
              } q-px-sm q-py-xxs rounded" title="Status">${
                useChangeCase(status_cd, "capitalCase").value
              }</span>`
            : "-",
        cellClass: "q-py-xxs rounded",
      },
    ];
  });

  const tableMappings = computed(() => {
    return {
      credit_notes: {
        heading: "Associated Credit Notes",
        columns: unref(columns),
      },
      debit_notes: {
        heading: "Associated Debit Notes",
        columns: unref(columns),
      },
      files: {
        heading: `${unref(formData).files?.length} Associated File(s)`,
        columns: unref(filesColumns),
      },
      associated_payments: {
        heading: "Payments Made",
        columns: unref(paymentsMadeColumns),
      },
    };
  });

  const viewAssociatedData = async (type) => {
    try {
      const rows = await getTableData(type);

      if (!rows.length) {
        return;
      }

      toggleAssociatedDataDrawer({
        edit: false,
        level: 0,
        open: true,
        view: true,
        props: {
          ...unref(tableMappings)?.[type],
          rows,
          type,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  return {
    viewAssociatedData,
  };
}
